import { EventBus } from '~/rage/common';
import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	ClientError,
	ClientErrorCode,
	ICharacterHouseClient,
	ModalButtonColor,
	ModalType,
	WalletType,
} from '~/shared';
import { BackendService, ICharacterProfile } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IModalService } from '../ModalService';
import { INotificationsService } from '../NotificationsService';
import { IPlayerService } from '../PlayerService';
import { IRpcService } from '../RpcService';
import { IHouseData, IHousesService } from './housesServiceTypes';

export class HousesService implements IHousesService {
	private readonly currentHouse = new Subject<IHouseData>({
		id: '',
		dayPrice: 0,
		daysMax: 0,
		daysPaid: 0,
		garagePlaces: 0,
		houseClassColor: '',
		houseClassName: '',
		isLocked: false,
		isOwned: false,
		sellPrice: 0,
		buyPrice: 0,
	});
	private exitTimeout?: NodeJS.Timeout;

	constructor(
		private readonly backend: BackendService,
		private readonly player: IPlayerService,
		private readonly gameConfig: GameConfigService,
		private readonly rpc: IRpcService,
		private readonly notifications: INotificationsService,
		private readonly modal: IModalService,
	) {
		rpc.register('houses:onEnter', async ({ id }: { id: string }) => {
			if (this.exitTimeout) {
				clearTimeout(this.exitTimeout);
				this.exitTimeout = undefined;
			}
			await this.loadHouse(id);
		});

		rpc.register('houses:onExit', () => {
			this.exitTimeout = setTimeout(() => {
				this.currentHouse.next({
					id: '',
					dayPrice: 0,
					daysMax: 0,
					daysPaid: 0,
					garagePlaces: 0,
					houseClassColor: '',
					houseClassName: '',
					isLocked: false,
					isOwned: false,
					sellPrice: 0,
					buyPrice: 0,
				});
				this.exitTimeout = undefined;
			}, 500);
		});

		this.player.waitForCharacterSelect().then(async () => {
			const [housesForSale, housesOwned] = await Promise.all([
				this.backend.invoke<{ houses: string[] }>('houses:getForSale'),
				this.backend.invoke<{ houses: string[] }>('houses:getOwned'),
			]);

			EventBus.triggerClient('houses:load', {
				housesForSale: housesForSale.houses,
				housesOwned: housesOwned.houses,
			});
		});

		this.backend.addEventHandler(
			'houses:daysPaid',
			(data: { houseId: string; days: number }) => {
				if (data.houseId === this.currentHouse.getValue().id) {
					this.currentHouse.next({
						...this.currentHouse.getValue(),
						daysPaid: data.days,
					});
				}
			},
		);

		this.backend.addEventHandler(
			'houses:stateChanged',
			(data: { id: string; owner: string | null }) => {
				const currentHouseId = this.currentHouse.getValue().id;
				if (data.id === currentHouseId) {
					this.loadHouse(data.id);
				}
			},
		);
	}

	public getMaxPaidDays(): number {
		return this.gameConfig.configs?.houses?.common?.maxPaidDays ?? 0;
	}

	@notifyOnError
	public getPriceForDay(houseId: string): number {
		const houseConfig = this.gameConfig.configs?.houses?.locations?.find(
			(house) => house.id === houseId,
		);
		if (!houseConfig) {
			throw new ClientError(ClientErrorCode.HouseNotFound);
		}

		const houseClassConfig = this.gameConfig.configs?.houses?.classes?.find(
			(houseClass) => houseClass.id === houseConfig.class,
		);
		if (!houseClassConfig) {
			throw new ClientError(ClientErrorCode.HouseNotFound);
		}

		return houseClassConfig.dayPrice;
	}

	public getCurrentHouse(): IObservable<IHouseData> {
		return this.currentHouse;
	}

	public async getOwnedHousesIds(): Promise<string[]> {
		const result = await this.backend.invoke<{ houses: string[] }>(
			'houses:getOwned',
		);

		return result.houses;
	}

	@notifyOnError
	public async buy(houseId: string, walletType: WalletType): Promise<void> {
		await this.backend.invoke('houses:buy', { houseId, walletType });
		this.currentHouse.next({
			...this.currentHouse.getValue(),
			isOwned: true,
		});
		await this.loadHouse(houseId);
		this.notifications.showSuccess('Вы приобрели дом');
	}

	@notifyOnError
	public async pay(houseId: string, days: number): Promise<void> {
		await this.backend.invoke('houses:pay', { houseId, days });
		await this.loadHouse(houseId);
		this.notifications.showSuccess(`Вы оплатили налоги`);
	}

	@notifyOnError
	public async sell(houseId: string): Promise<void> {
		await this.backend.invoke('houses:sell', { houseId });
		this.notifications.showSuccess('Вы продали дом');
		this.exitMenu();
	}

	@notifyOnError
	public async inviteToBuyHouse(
		houseId: string,
		otherCharacterId: string,
		price: number,
	): Promise<void> {
		if (price <= 0) {
			this.notifications.showError('Укажите цену продажи');
			return;
		}

		const character = await this.backend.invoke<ICharacterProfile>(
			'characters:getById',
			{ characterId: otherCharacterId },
		);
		if (!character) {
			throw new ClientError(ClientErrorCode.PlayerNotFound);
		}

		const { isAccepted } = await this.modal.showModalAsync({
			type: ModalType.Price,
			title: 'Подтверждение',
			text: `Вы действительно хотите продать дом #${houseId} игроку ${
				character.nickname
			} за ${price.toLocaleString('ru-RU')} руб.?`,
			price: price,
			acceptButton: {
				color: ModalButtonColor.Success,
				text: 'Продать',
			},
			cancelButton: {
				color: ModalButtonColor.Secondary,
				text: 'Отмена',
			},
			wallet: WalletType.Cash,
		});
		if (!isAccepted) {
			return;
		}

		await this.backend.invoke('houses:inviteToBuyHouse', {
			houseId,
			otherCharacterId,
			price,
		});
		this.notifications.showSuccess(
			'Игроку было отправлено предложение купить ваш дом',
		);
	}

	@notifyOnError
	public async buyFromCharacter(
		houseId: string,
		characterId: string,
		isAccept: boolean,
	): Promise<void> {
		await this.backend.invoke('houses:buyFromCharacter', {
			houseId,
			characterId,
			isAccept,
		});
		this.notifications.showSuccess('Вы приобрели дом');
	}

	public async enterHouse(): Promise<void> {
		return await this.rpc
			.invokeClient('houses:enter', this.getCurrentHouse()?.getValue()?.id)
			.then(() => this.exitMenu())
			.catch((error) => {
				this.notifications.showException(error);
			});
	}

	@notifyOnError
	public async setHouseLocked(
		houseId: string,
		isLocked: boolean,
	): Promise<void> {
		await this.rpc.invokeServer('houses:setLocked', { houseId, isLocked });

		this.currentHouse.next({
			...this.currentHouse.getValue(),
			isLocked,
		});

		this.notifications.showSuccess(
			isLocked ? 'Двери дома закрыты' : 'Двери дома открыты',
		);
	}

	public async exitMenu(): Promise<void> {
		return await this.rpc.invokeClient('houses:exitMenu');
	}

	@notifyOnError
	public async getById(houseId: string): Promise<ICharacterHouseClient | null> {
		const result = await this.backend.invoke<{
			house: ICharacterHouseClient | null;
		}>('houses:getById', { houseId });

		return result.house;
	}

	@notifyOnError
	private async loadHouse(houseId: string): Promise<void> {
		const housesCommon = this.gameConfig.configs?.houses?.common;
		const houseConfig = this.gameConfig.configs?.houses?.locations?.find(
			(house) => house.id === houseId,
		);
		if (!houseConfig) {
			throw new Error('House not found');
		}
		const houseClassConfig = this.gameConfig.configs?.houses?.classes?.find(
			(houseClass) => houseClass.id === houseConfig.class,
		);
		if (!houseClassConfig) {
			throw new Error('House class not found');
		}

		const [characterHouse, isLocked] = await Promise.all([
			this.getById(houseId),
			this.rpc.invokeServer('houses:getLocked', { houseId }),
		]);

		let character: ICharacterProfile | null = null;
		let sellPrice = 0;
		if (characterHouse) {
			character = await this.backend.invoke<ICharacterProfile>(
				'characters:getById',
				{ characterId: characterHouse.characterId },
			);
			sellPrice = Math.round(
				characterHouse.buyPrice * (housesCommon?.govSellPricePercent ?? 0),
			);
		}

		const localCharacterId = this.player.getCharacterId().getValue();
		this.currentHouse.next({
			id: houseId,
			buyPrice: houseConfig.price,
			dayPrice: houseClassConfig.dayPrice,
			daysMax: housesCommon?.maxPaidDays ?? 0,
			daysPaid: characterHouse?.daysPaid ?? 0,
			garagePlaces: houseConfig.parkingSlots,
			houseClassColor: houseClassConfig.color,
			houseClassName: houseClassConfig.name,
			isLocked: !!isLocked,
			isOwned: localCharacterId
				? characterHouse?.characterId === localCharacterId
				: false,
			sellPrice,
			ownerId: characterHouse?.characterId,
			ownerNickname: character?.nickname,
		});
	}
}
